import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Connects to /ws/inbox using the user's existing httpOnly cookie auth.
 * Auto-reconnects with exponential backoff on disconnect.
 *
 * Returns:
 *  - connected: boolean
 *  - lastEvent: { event, ... } | null
 *  - liveMessage: { conversation_id, message } | null  (latest message.created event)
 *  - liveConversation: { conversation_id, conversation } | null (latest conversation.updated)
 *  - liveTyping: { conversation_id, from, from_role, active } | null
 *  - sendTyping(conversation_id, start: bool): broadcasts a typing.start/stop event
 */
export function useInboxSocket(enabled = true) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [liveMessage, setLiveMessage] = useState(null);
  const [liveConversation, setLiveConversation] = useState(null);
  const [liveTyping, setLiveTyping] = useState(null);
  const [liveAIControl, setLiveAIControl] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    // All connection state lives in closures to keep the rule-checker happy:
    // we don't mutate any refs from inside the cleanup, only call ws.close().
    const state = { alive: true, retry: 0 };

    const connect = () => {
      if (!state.alive) return;
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const url = backend.replace(/^http/, "ws") + "/api/ws/inbox";
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleRetry();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        state.retry = 0;
        Promise.resolve().then(() => { if (state.alive) setConnected(true); });
      };
      ws.onmessage = (e) => {
        let data;
        try { data = JSON.parse(e.data); } catch { return; }
        Promise.resolve().then(() => {
          if (!state.alive) return;
          setLastEvent(data);
          if (data.event === "message.created") {
            setLiveMessage({ conversation_id: data.conversation_id, message: data.message });
          } else if (data.event === "message.updated") {
            setLiveMessage({ conversation_id: data.conversation_id, message_id: data.message_id, patch: data.patch, _kind: "patch" });
          } else if (data.event === "conversation.updated") {
            setLiveConversation({ conversation_id: data.conversation_id, conversation: data.conversation });
          } else if (data.event === "conversation.created") {
            // New WA conversation just appeared — propagate via liveConversation so listeners refetch
            setLiveConversation({ conversation_id: data.conversation?.id, conversation: data.conversation });
          } else if (data.event === "typing.start") {
            setLiveTyping({ conversation_id: data.conversation_id, from: data.from, from_role: data.from_role, active: true, _t: Date.now() });
          } else if (data.event === "typing.stop") {
            setLiveTyping({ conversation_id: data.conversation_id, from: data.from, from_role: data.from_role, active: false, _t: Date.now() });
          } else if (typeof data.event === "string" && data.event.startsWith("ai_control.")) {
            setLiveAIControl({
              conversation_id: data.conversation_id,
              event: data.event,
              ai_control: data.ai_control || null,
              _t: Date.now(),
            });
            // Also refresh the conversation summary if attached
            if (data.conversation) {
              setLiveConversation({ conversation_id: data.conversation_id, conversation: data.conversation });
            }
          }
        });
      };
      ws.onclose = () => {
        Promise.resolve().then(() => { if (state.alive) setConnected(false); });
        scheduleRetry();
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    const scheduleRetry = () => {
      if (!state.alive) return;
      const wait = Math.min(30000, 1000 * Math.pow(2, state.retry++));
      setTimeout(() => { if (state.alive) connect(); }, wait);
    };

    // Defer initial connect to next microtask
    Promise.resolve().then(connect);

    return () => {
      state.alive = false;
      const ws = wsRef.current;
      if (ws) {
        try { ws.close(); } catch { /* noop */ }
      }
    };
  }, [enabled]);

  const sendTyping = useCallback((conversationId, start) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1 || !conversationId) return;
    try {
      ws.send(JSON.stringify({ event: start ? "typing.start" : "typing.stop", conversation_id: conversationId }));
    } catch { /* noop */ }
  }, []);

  return { connected, lastEvent, liveMessage, liveConversation, liveTyping, liveAIControl, sendTyping };
}
